import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Cropper from "react-easy-crop";
import {
  getOrganization,
  getTemplate,
  updateTemplate,
  uploadTemplateCover,
  getTemplateBlocks,
  createTemplateBlock,
  updateTemplateBlock,
  deleteTemplateBlock,
  reorderTemplateBlocks,
} from "../services/api";
import { useTranslation } from "../i18n/LanguageContext";
import {
  ArrowLeft,
  Upload,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  Palette,
  Type,
  ImageIcon,
  Phone,
  Share2,
  FileText,
  MousePointer,
  Globe,
  MapPin,
  Mail,
  Info,
} from "lucide-react";

/* ============================================================
   CONSTANTS
   ============================================================ */

const BLOCK_TYPE_ICONS = {
  about: Info,
  contact_info: Phone,
  social_links: Share2,
  gallery: ImageIcon,
  custom_text: FileText,
  cta_button: MousePointer,
  website: Globe,
  location: MapPin,
  contact_form: Mail,
};

const BLOCK_TYPES = [
  { value: "about", label: "About" },
  { value: "contact_info", label: "Contact Info" },
  { value: "social_links", label: "Social Links" },
  { value: "gallery", label: "Gallery" },
  { value: "custom_text", label: "Custom Text" },
  { value: "cta_button", label: "CTA Button" },
  { value: "website", label: "Website" },
  { value: "location", label: "Location" },
  { value: "contact_form", label: "Contact Form" },
];

const PRESET_COLORS = [
  "#3B82F6", "#2563EB", "#1D4ED8", "#EF4444", "#DC2626",
  "#10B981", "#059669", "#F59E0B", "#D97706", "#8B5CF6",
  "#7C3AED", "#EC4899", "#DB2777", "#06B6D4", "#0891B2",
  "#F97316", "#EA580C", "#6366F1", "#14B8A6", "#0D9488",
  "#84CC16", "#E11D48", "#000000", "#6B7280",
];

const THEME_STYLE_KEYS = [
  { value: "modern", tKey: "editor.theme.modern" },
  { value: "classic", tKey: "editor.theme.classic" },
  { value: "minimal", tKey: "editor.theme.minimal" },
  { value: "bold", tKey: "editor.theme.bold" },
  { value: "pro", tKey: "editor.theme.pro" },
  { value: "instagram", tKey: "editor.theme.instagram" },
  { value: "instagram_dark", tKey: "editor.theme.instagramDark" },
];

/* ============================================================
   IMAGE CROP HELPERS
   ============================================================ */

function createImage(url) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", reject);
    img.src = url;
  });
}

async function getCroppedBlob(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height);
  return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), "image/png"));
}

function ImageCropModal({ imageSrc, aspect, onConfirm, onCancel, labels }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const onCropComplete = useCallback((_, pixels) => setCroppedAreaPixels(pixels), []);
  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    const blob = await getCroppedBlob(imageSrc, croppedAreaPixels);
    onConfirm(blob);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        <div className="relative h-80 bg-gray-900">
          <Cropper image={imageSrc} crop={crop} zoom={zoom} aspect={aspect} cropShape="rect" onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropComplete} />
        </div>
        <div className="px-5 py-3">
          <label className="flex items-center gap-3 text-sm text-gray-500">
            {labels.zoom}
            <input type="range" min={1} max={3} step={0.05} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="flex-1" />
          </label>
        </div>
        <div className="flex justify-end gap-3 px-5 py-3 border-t border-gray-100">
          <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">{labels.cancel}</button>
          <button onClick={handleConfirm} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700">{labels.cropUpload}</button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   THEME PREVIEW (same as ProfileEditorPage)
   ============================================================ */

function ThemePreview({ style, color, name, tagline }) {
  const { t } = useTranslation();
  const configs = {
    modern: { page: "bg-gray-50 rounded-xl", coverGradient: `linear-gradient(135deg, ${color}, ${color}cc)`, coverH: "h-16", avatar: "rounded-full border-4 border-white shadow-lg", name: "text-sm font-bold text-gray-900", tagline: "text-xs text-gray-500", badge: "rounded-full text-white", card: "bg-white rounded-lg shadow-sm p-3", sectionTitle: "text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1", contactBg: "bg-gray-50 rounded-md", iconShape: "rounded-full" },
    classic: { page: "bg-gray-100 rounded-sm border border-gray-200", coverGradient: `linear-gradient(180deg, ${color}, ${color}55)`, coverH: "h-14", avatar: "rounded-sm border-4 border-white shadow-md", name: "text-sm font-bold text-gray-900 font-serif", tagline: "text-xs text-gray-500 italic font-serif", badge: "rounded-sm border border-gray-400 text-gray-600 bg-transparent", card: "bg-white rounded-sm border border-gray-200 p-3", sectionTitle: "text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] mb-1 border-b border-gray-200 pb-1", contactBg: "border-b border-gray-100", iconShape: "rounded-sm" },
    minimal: { page: "bg-white rounded-xl border border-gray-100", coverGradient: `${color}11`, coverH: "h-8", avatar: "rounded-full border-2 border-gray-200", name: "text-sm font-light text-gray-800 tracking-wide", tagline: "text-xs text-gray-400 font-light", badge: "rounded-full border border-gray-200 text-gray-500 bg-transparent", card: "bg-transparent border-b border-gray-100 p-3", sectionTitle: "text-[10px] font-medium text-gray-300 uppercase tracking-wider mb-1", contactBg: "", iconShape: "rounded-full border border-gray-200" },
    bold: { page: "bg-gray-950 rounded-xl", coverGradient: `linear-gradient(160deg, ${color}, #000)`, coverH: "h-16", avatar: "rounded-xl border-4 border-gray-800 shadow-xl", name: "text-sm font-black text-white uppercase tracking-tight", tagline: "text-xs text-gray-400 font-medium", badge: "rounded-lg text-white", card: "bg-gray-900 rounded-xl border border-gray-800 p-3", sectionTitle: "text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1", contactBg: "rounded-lg bg-gray-800/50", iconShape: "rounded-xl" },
    pro: { page: "bg-gray-950 rounded-xl relative overflow-hidden", coverGradient: `linear-gradient(135deg, ${color}88, ${color}22)`, coverH: "h-14", avatar: "rounded-2xl border-2 border-white/20 shadow-2xl", name: "text-sm font-bold text-white", tagline: "text-xs text-gray-400", badge: "rounded-full text-white/70 bg-white/10", card: "bg-white/5 backdrop-blur-md rounded-xl border border-white/10 p-3", sectionTitle: "text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1", contactBg: "rounded-lg bg-white/5", iconShape: "rounded-xl", meshBlob: true },
    instagram: { page: "bg-white rounded-xl border border-gray-200", coverGradient: "transparent", coverH: "h-0", avatar: "rounded-full", name: "text-xs font-semibold text-gray-900", tagline: "text-xs text-gray-500", badge: "rounded text-gray-500 bg-gray-100", card: "bg-white border-b border-gray-200 p-3", sectionTitle: "text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1", contactBg: "rounded-md bg-gray-50", iconShape: "rounded-full", igRing: true, noOverlap: true },
    instagram_dark: { page: "bg-gray-950 rounded-xl border border-gray-800", coverGradient: "transparent", coverH: "h-0", avatar: "rounded-full", name: "text-xs font-semibold text-white", tagline: "text-xs text-gray-300", badge: "rounded text-gray-300 bg-gray-800", card: "bg-gray-950 border-b border-gray-800 p-3", sectionTitle: "text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1", contactBg: "rounded-md bg-gray-900", iconShape: "rounded-full", igRing: true, igDark: true, noOverlap: true },
  };
  const c = configs[style] || configs.modern;
  const isDark = ["bold", "pro", "instagram_dark"].includes(style);
  const isIg = style === "instagram" || style === "instagram_dark";
  const isPro = style === "pro";

  return (
    <div className={`overflow-hidden ${c.page}`}>
      {c.meshBlob && (
        <>
          <div className="absolute top-0 left-0 w-1/2 h-1/2 rounded-full opacity-20 blur-[40px]" style={{ background: color }} />
          <div className="absolute bottom-0 right-0 w-1/3 h-1/3 rounded-full opacity-15 blur-[30px]" style={{ background: color }} />
        </>
      )}
      {c.coverH !== "h-0" && <div className={`w-full ${c.coverH}`} style={{ background: c.coverGradient }} />}
      <div className={`px-4 pb-4 relative ${c.noOverlap || c.coverH === "h-0" ? "pt-4" : "-mt-5"}`}>
        {isIg ? (
          <div className="flex items-center gap-3 mb-3">
            <div className="flex-shrink-0" style={{ background: "linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)", padding: "2px", borderRadius: "9999px" }}>
              <div className={`w-10 h-10 ${c.avatar} border-2 ${c.igDark ? "border-gray-950" : "border-white"} flex items-center justify-center text-white text-xs font-bold`} style={{ backgroundColor: color }}>{name.charAt(0)?.toUpperCase()}</div>
            </div>
            <div><div className={c.name}>{name}</div><div className={c.tagline}>{tagline}</div></div>
          </div>
        ) : isPro ? (
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-10 h-10 flex-shrink-0 ${c.avatar} flex items-center justify-center text-white text-xs font-bold`} style={{ backgroundColor: color }}>{name.charAt(0)?.toUpperCase()}</div>
            <div><div className={c.name}>{name}</div><div className={c.tagline}>{tagline}</div></div>
          </div>
        ) : (
          <>
            <div className="flex justify-center">
              <div className={`w-10 h-10 ${c.avatar} flex items-center justify-center text-white text-sm font-bold`} style={{ backgroundColor: color }}>{name.charAt(0)?.toUpperCase()}</div>
            </div>
            <div className="text-center mt-2">
              <div className={c.name}>{name}</div>
              <div className={c.tagline}>{tagline}</div>
              <span className={`inline-block mt-1 px-2 py-0.5 text-[9px] font-medium ${c.badge}`} style={c.badge.includes("bg-transparent") || c.badge.includes("bg-white") || c.badge.includes("bg-gray") ? {} : { backgroundColor: color }}>{t("editor.preview.badge")}</span>
            </div>
          </>
        )}
        <div className={`${isIg || isPro ? "" : "mt-3"} space-y-2`}>
          <div className={c.card}>
            <div className={c.sectionTitle}>{t("editor.preview.contact")}</div>
            <div className="space-y-1">
              {["email@example.com", "+1 234 5678"].map((val, i) => (
                <div key={i} className={`flex items-center gap-2 py-1 px-1 ${c.contactBg}`}>
                  <div className={`w-5 h-5 ${c.iconShape} flex items-center justify-center flex-shrink-0`} style={{ backgroundColor: c.iconShape.includes("border") ? "transparent" : color, borderColor: color }}>
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: c.iconShape.includes("border") ? color : "white" }} />
                  </div>
                  <span className={`text-[10px] ${isDark ? "text-gray-300" : "text-gray-600"}`}>{val}</span>
                </div>
              ))}
            </div>
          </div>
          {isIg ? (
            <div className="grid grid-cols-3 gap-[1px]">
              {[1, 2, 3, 4, 5, 6].map((i) => (<div key={i} className={`aspect-square ${c.igDark ? "bg-gray-800" : "bg-gray-100"}`} />))}
            </div>
          ) : (
            <div className={c.card}>
              <div className={c.sectionTitle}>{t("editor.preview.social")}</div>
              <div className="flex gap-1.5">
                {[1, 2, 3].map((i) => (
                  <div key={i} className={`w-6 h-6 ${c.iconShape} flex items-center justify-center`} style={{ backgroundColor: c.iconShape.includes("border") ? "transparent" : color, borderColor: color }}>
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: c.iconShape.includes("border") ? color : "white" }} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   MAIN COMPONENT
   ============================================================ */

export default function TemplateEditorPage() {
  const { orgId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const coverImageRef = useRef(null);

  const THEME_STYLES = THEME_STYLE_KEYS.map((ts) => ({ ...ts, label: t(ts.tKey) }));

  const [org, setOrg] = useState(null);
  const [template, setTemplate] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [addBlockOpen, setAddBlockOpen] = useState(false);
  const [cropModal, setCropModal] = useState(null);

  useEffect(() => {
    loadData();
  }, [orgId]);

  const loadData = async () => {
    try {
      const [orgRes, templateRes, blocksRes] = await Promise.all([
        getOrganization(orgId),
        getTemplate(orgId),
        getTemplateBlocks(orgId),
      ]);
      setOrg(orgRes.data.organization);
      setTemplate(templateRes.data.template);
      setBlocks(blocksRes.data.blocks);
    } catch {
      navigate(`/app/org/${orgId}`);
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSaveTemplate = async (updates) => {
    setSaving(true);
    try {
      const res = await updateTemplate(orgId, updates);
      setTemplate(res.data.template);
      showToast(t("template.saved"));
    } catch {
      showToast(t("template.saveFailed"), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleCoverSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    const reader = new FileReader();
    reader.onload = () => setCropModal({ src: reader.result });
    reader.readAsDataURL(file);
  };

  const handleCroppedUpload = async (blob) => {
    setCropModal(null);
    const formData = new FormData();
    formData.append("file", blob, "cover.jpg");
    try {
      const res = await uploadTemplateCover(orgId, formData);
      setTemplate(res.data.template);
      showToast(t("template.coverUploaded"));
    } catch {
      showToast(t("template.uploadFailed"), "error");
    }
  };

  const handleAddBlock = async (blockType) => {
    setAddBlockOpen(false);
    try {
      const res = await createTemplateBlock(orgId, { block_type: blockType });
      setBlocks((prev) => [...prev, res.data.block]);
      showToast(t("template.blockAdded"));
    } catch {
      showToast(t("template.blockAddFailed"), "error");
    }
  };

  const handleDeleteBlock = async (blockId) => {
    try {
      await deleteTemplateBlock(blockId);
      setBlocks((prev) => prev.filter((b) => b.id !== blockId));
      showToast(t("template.blockDeleted"));
    } catch {
      showToast(t("template.blockDeleteFailed"), "error");
    }
  };

  const handleToggleVisibility = async (block) => {
    try {
      const res = await updateTemplateBlock(block.id, { is_visible: !block.is_visible });
      setBlocks((prev) => prev.map((b) => (b.id === block.id ? res.data.block : b)));
    } catch {
      showToast("Error", "error");
    }
  };

  const handleUpdateBlockTitle = async (block, title) => {
    try {
      await updateTemplateBlock(block.id, { title });
      setBlocks((prev) => prev.map((b) => (b.id === block.id ? { ...b, title } : b)));
    } catch {
      showToast("Error", "error");
    }
  };

  const handleMoveBlock = async (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= blocks.length) return;
    const newBlocks = [...blocks];
    [newBlocks[index], newBlocks[newIndex]] = [newBlocks[newIndex], newBlocks[index]];
    setBlocks(newBlocks);
    try {
      await reorderTemplateBlocks(orgId, newBlocks.map((b) => b.id));
    } catch {
      showToast("Error", "error");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!template) return null;

  const tc = template.theme_color || "#3B82F6";

  return (
    <div className="max-w-2xl mx-auto">
      {/* Back */}
      <button
        onClick={() => navigate(`/app/org/${orgId}`)}
        className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-4 transition"
      >
        <ArrowLeft className="w-4 h-4" />
        {org?.name || t("template.back")}
      </button>

      <h1 className="text-2xl font-bold text-gray-900 mb-1">{t("template.title")}</h1>
      <p className="text-sm text-gray-500 mb-6">{t("template.description")}</p>

      {/* Toast */}
      {toast && (
        <div className={`mb-4 p-3 rounded-lg text-sm font-medium ${toast.type === "error" ? "bg-red-50 text-red-700 border border-red-200" : "bg-green-50 text-green-700 border border-green-200"}`}>
          {toast.message}
        </div>
      )}

      {/* Crop Modal */}
      {cropModal && (
        <ImageCropModal
          imageSrc={cropModal.src}
          aspect={16 / 5}
          onConfirm={handleCroppedUpload}
          onCancel={() => setCropModal(null)}
          labels={{ zoom: t("editor.zoom"), cancel: t("editor.cropCancel"), cropUpload: t("editor.cropUpload") }}
        />
      )}

      <div className="space-y-8">
        {/* Tagline */}
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-1">{t("template.tagline")}</label>
          <p className="text-xs text-gray-500 mb-3">{t("template.taglineDesc")}</p>
          <input
            type="text"
            defaultValue={template.tagline || ""}
            key={template.tagline}
            onBlur={(e) => {
              if (e.target.value !== (template.tagline || "")) {
                handleSaveTemplate({ tagline: e.target.value });
              }
            }}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder={t("template.taglinePlaceholder")}
          />
        </div>

        {/* Cover Image Upload */}
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-3">
            {t("template.coverImage")}
          </label>
          <div
            onClick={() => coverImageRef.current?.click()}
            className="w-full h-48 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition overflow-hidden bg-gray-50"
          >
            {template.cover_image ? (
              <img src={template.cover_image} alt="Cover" className="w-full h-full object-cover" />
            ) : (
              <div className="flex flex-col items-center text-gray-400">
                <Upload className="w-8 h-8 mb-2" />
                <span className="text-sm">{t("template.uploadCover")}</span>
              </div>
            )}
          </div>
          <p className="text-xs text-gray-400 mt-1.5">{t("editor.coverImageRec")}</p>
          <input ref={coverImageRef} type="file" accept="image/*" onChange={handleCoverSelect} className="hidden" />
        </div>

        {/* Theme Color */}
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-3">
            {t("template.themeColor")}
          </label>
          {/* Current color preview */}
          <div className="flex items-center gap-3 mb-3 p-3 bg-gray-50 rounded-xl">
            <div className="w-10 h-10 rounded-xl shadow-inner border border-black/10 flex-shrink-0" style={{ backgroundColor: tc }} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-700">{t("editor.currentColor")}</div>
              <div className="text-xs font-mono text-gray-400 uppercase">{tc}</div>
            </div>
          </div>
          {/* Preset swatches */}
          <div className="flex flex-wrap gap-2 mb-3">
            {PRESET_COLORS.map((color) => (
              <button
                key={color}
                onClick={() => handleSaveTemplate({ theme_color: color })}
                className={`w-8 h-8 rounded-lg border-2 transition hover:scale-110 ${
                  tc === color ? "border-gray-900 ring-2 ring-offset-2 ring-gray-400" : "border-gray-200"
                }`}
                style={{ backgroundColor: color }}
                title={color}
              />
            ))}
          </div>
          {/* Custom color row */}
          <div className="flex items-center gap-2">
            <label className="relative cursor-pointer">
              <input
                type="color"
                value={tc}
                onChange={(e) => handleSaveTemplate({ theme_color: e.target.value })}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              <div className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 border border-gray-200 bg-white rounded-lg hover:bg-gray-50 transition">
                <Palette className="w-3.5 h-3.5" />
                {t("editor.customColor")}
              </div>
            </label>
            <div className="flex items-center border border-gray-200 rounded-lg bg-white overflow-hidden">
              <span className="px-2 py-1.5 text-xs text-gray-400 bg-gray-50 border-r border-gray-200">#</span>
              <input
                type="text"
                maxLength={6}
                defaultValue={tc.replace("#", "")}
                key={tc}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^0-9a-fA-F]/g, "").slice(0, 6);
                  e.target.value = v;
                  if (v.length === 6) {
                    handleSaveTemplate({ theme_color: `#${v}` });
                  }
                }}
                className="w-[5.5rem] px-2 py-1.5 text-sm font-mono text-gray-700 border-none outline-none uppercase"
                placeholder="3B82F6"
              />
            </div>
          </div>
        </div>

        {/* Theme Style */}
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-3">
            {t("template.themeStyle")}
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {THEME_STYLES.map((ts) => {
              const selected = template.theme_style === ts.value;
              const previewStyles = {
                modern: { bg: "bg-gray-50", cover: `linear-gradient(135deg, ${tc}, ${tc}cc)`, avatar: "rounded-full", card: "bg-white rounded-lg shadow-sm", text: "font-sans" },
                classic: { bg: "bg-gray-100", cover: `linear-gradient(180deg, ${tc}, ${tc}55)`, avatar: "rounded-sm", card: "bg-white rounded-sm border border-gray-200", text: "font-serif" },
                minimal: { bg: "bg-white", cover: `${tc}11`, avatar: "rounded-full", card: "bg-transparent border-b border-gray-100", text: "font-light" },
                bold: { bg: "bg-gray-950", cover: `linear-gradient(160deg, ${tc}, #000)`, avatar: "rounded-xl", card: "bg-gray-900 rounded-xl border border-gray-800", text: "font-black text-white" },
                pro: { bg: "bg-gray-950", cover: `linear-gradient(135deg, ${tc}44, ${tc}11)`, avatar: "rounded-2xl", card: "bg-white/10 rounded-xl border border-white/10", text: "font-bold text-white" },
                instagram: { bg: "bg-white", cover: `linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)`, avatar: "rounded-full ring-2 ring-pink-500 ring-offset-1", card: "bg-white border-b border-gray-200", text: "font-semibold" },
                instagram_dark: { bg: "bg-gray-950", cover: `linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)`, avatar: "rounded-full ring-2 ring-pink-500 ring-offset-1 ring-offset-gray-950", card: "bg-gray-950 border-b border-gray-800", text: "font-semibold text-white" },
              };
              const p = previewStyles[ts.value] || previewStyles.modern;
              const isIgThumb = ts.value === "instagram" || ts.value === "instagram_dark";
              const isIgDarkThumb = ts.value === "instagram_dark";
              const isProThumb = ts.value === "pro";
              const isDarkThumb = ["bold", "pro", "instagram_dark"].includes(ts.value);
              return (
                <button
                  key={ts.value}
                  onClick={() => handleSaveTemplate({ theme_style: ts.value })}
                  className={`overflow-hidden rounded-xl border-2 text-left transition flex flex-col justify-between ${
                    selected ? "border-blue-600 ring-1 ring-blue-600" : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className={`${p.bg} px-2 pb-2 relative overflow-hidden flex-1`}>
                    {isProThumb && <div className="absolute top-0 left-0 w-1/2 h-full rounded-full opacity-20 blur-[15px]" style={{ background: tc }} />}
                    {!isIgThumb && <div className="h-6 rounded-t relative" style={{ background: p.cover }} />}
                    {isIgThumb ? (
                      <div className="relative">
                        <div className="flex items-center gap-1.5 mb-2">
                          <div style={{ background: "linear-gradient(45deg, #f09433, #dc2743, #bc1888)", padding: "1.5px", borderRadius: "9999px" }}>
                            <div className={`w-6 h-6 rounded-full border-[1.5px] ${isIgDarkThumb ? "border-gray-950" : "border-white"}`} style={{ backgroundColor: tc }} />
                          </div>
                          <div>
                            <div className={`w-10 h-1 rounded ${isIgDarkThumb ? "bg-gray-600" : "bg-gray-300"}`} />
                            <div className={`w-7 h-0.5 rounded ${isIgDarkThumb ? "bg-gray-700" : "bg-gray-200"} mt-0.5`} />
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-[1px]">
                          {[1,2,3,4,5,6].map((i) => (<div key={i} className={`aspect-square ${isIgDarkThumb ? "bg-gray-800" : "bg-gray-100"} rounded-[1px]`} />))}
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex flex-col items-center -mt-3">
                          <div className={`w-6 h-6 ${p.avatar} border-2 border-white`} style={{ backgroundColor: tc }} />
                          <div className={`mt-1 w-12 h-1 rounded ${isDarkThumb ? "bg-gray-600" : "bg-gray-300"}`} />
                          <div className={`mt-1 w-8 h-0.5 rounded ${isDarkThumb ? "bg-gray-700" : "bg-gray-200"}`} />
                        </div>
                        <div className={`mt-2 mx-0.5 p-1.5 ${p.card}`}>
                          <div className={`w-8 h-0.5 rounded mb-1 ${isDarkThumb ? "bg-gray-600" : "bg-gray-200"}`} />
                          <div className={`w-full h-0.5 rounded ${isDarkThumb ? "bg-gray-700" : "bg-gray-100"}`} />
                        </div>
                      </>
                    )}
                  </div>
                  <div className={`py-2 text-center border-t ${selected ? "bg-blue-50 border-blue-100" : "bg-gray-50 border-gray-100"}`}>
                    <div className={`text-sm font-medium ${selected ? "text-blue-700" : "text-gray-700"}`}>{ts.label}</div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Live Preview */}
          <div className="mt-6">
            <label className="block text-sm font-semibold text-gray-900 mb-3">{t("editor.themePreview")}</label>
            <ThemePreview
              style={template.theme_style}
              color={tc}
              name={org?.name || t("orgs.title")}
              tagline={template.tagline || "Your tagline"}
            />
          </div>
        </div>

        {/* Template Blocks */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <label className="block text-sm font-semibold text-gray-900">{t("template.blocks")}</label>
            <div className="relative">
              <button
                onClick={() => setAddBlockOpen(!addBlockOpen)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition"
              >
                <Plus className="w-3.5 h-3.5" />
                {t("template.addBlock")}
              </button>
              {addBlockOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setAddBlockOpen(false)} />
                  <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-lg shadow-lg border border-gray-200 z-20 py-1">
                    {BLOCK_TYPES.map((bt) => {
                      const Icon = BLOCK_TYPE_ICONS[bt.value] || Type;
                      return (
                        <button key={bt.value} onClick={() => handleAddBlock(bt.value)} className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                          <Icon className="w-4 h-4 text-gray-400" />
                          {bt.label}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>

          <p className="text-xs text-gray-500 mb-4">{t("template.blocksDesc")}</p>

          {blocks.length === 0 ? (
            <p className="text-center text-sm text-gray-400 py-8">{t("template.noBlocks")}</p>
          ) : (
            <div className="space-y-2">
              {blocks.map((block, index) => {
                const Icon = BLOCK_TYPE_ICONS[block.block_type] || Type;
                return (
                  <div
                    key={block.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition ${
                      block.is_visible ? "border-gray-200 bg-white" : "border-gray-100 bg-gray-50 opacity-60"
                    }`}
                  >
                    <div className="flex flex-col gap-0.5">
                      <button onClick={() => handleMoveBlock(index, -1)} disabled={index === 0} className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30">
                        <ChevronUp className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => handleMoveBlock(index, 1)} disabled={index === blocks.length - 1} className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30">
                        <ChevronDown className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <input
                      type="text"
                      defaultValue={block.title}
                      onBlur={(e) => { if (e.target.value !== block.title) handleUpdateBlockTitle(block, e.target.value); }}
                      className="flex-1 text-sm font-medium text-gray-700 bg-transparent border-none outline-none focus:ring-0 p-0"
                    />
                    <span className="text-[10px] text-gray-400 uppercase">{block.block_type.replace("_", " ")}</span>
                    <button onClick={() => handleToggleVisibility(block)} className="p-1 text-gray-400 hover:text-gray-600 transition" title={block.is_visible ? "Hide" : "Show"}>
                      {block.is_visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                    </button>
                    <button onClick={() => handleDeleteBlock(block.id)} className="p-1 text-gray-400 hover:text-red-500 transition">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
